import { useId, useState, type ReactNode } from 'react';
import { Icon } from '../icons/Icon';
import { Button, IconButton } from '../ui/controls';
import { DialogHead, Modal, useToast } from '../ui/overlays';
import { useAppEnv } from './context';
import {
  dismissInstallHint,
  IN_APP_NAMES,
  installHintDismissed,
  installPlan,
  MOBILE_GUIDE,
  needsHomeScreenForNotifications,
  useInstallOffer,
  type InstallPlan,
} from './install';
import { NotifyHint } from './NotifyHint';

/** The way this phone puts the messenger on its home screen, with the browser's install offer when it made one. */
export function useInstallPlan(): { plan: InstallPlan; install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>; installedHere: boolean } {
  const offer = useInstallOffer();
  return { plan: installPlan({ canPrompt: offer.canPrompt }), install: offer.prompt, installedHere: offer.installedHere };
}

/** What the plan asks of the person, in one sentence. */
function headline(plan: InstallPlan): string {
  switch (plan.kind) {
    case 'open-outside':
      return `${IN_APP_NAMES[plan.app]} 안에서는 홈 화면에 추가할 수 없어요. 휴대폰의 브라우저로 열어 주세요.`;
    case 'ios-share':
      return '홈 화면에 추가하면 앱처럼 열리고, 아이폰은 그래야 알림을 받아요.';
    case 'ios-safari-only':
      return '이 iOS에서는 Safari로 열어야 홈 화면에 추가할 수 있어요.';
    default:
      return '홈 화면에 추가하면 앱처럼 열리고 알림도 받아요.';
  }
}

/** The step list for plans the page cannot carry out itself. */
export function InstallSteps({ plan }: { plan: InstallPlan }) {
  const iosShare = (browser: string): ReactNode[] => [
    browser === 'chrome' ? (
      <>주소창 오른쪽의 <b>공유 버튼</b>(네모에서 위로 향한 화살표)을 눌러요.</>
    ) : browser === 'edge' || browser === 'firefox' ? (
      <>화면 아래 <b>⋯ 메뉴</b> → <b>공유</b>를 눌러요.</>
    ) : (
      <>
        <b>공유 버튼</b>(네모에서 위로 향한 화살표)을 눌러요. 보통 화면 아래에 있고, 보이지 않으면 주소창 옆 <b>⋯</b> 안에 있어요.
      </>
    ),
    <>목록을 내려 <b>홈 화면에 추가</b>를 눌러요.</>,
    <><b>웹 앱으로 열기</b>가 보이면 켠 채로 두고, 오른쪽 위 <b>추가</b>를 눌러요.</>,
    <>홈 화면의 메신저 아이콘으로 열고 한 번 더 로그인해요. 알림은 그 앱에서 켜요.</>,
  ];
  let steps: ReactNode[];
  switch (plan.kind) {
    case 'android-menu':
      steps =
        plan.browser === 'samsung'
          ? [
              <>화면 아래 <b>≡ 메뉴</b>를 눌러요.</>,
              <><b>현재 페이지 추가</b> → <b>홈 화면</b>을 눌러요.</>,
              <>홈 화면의 메신저 아이콘으로 열어요. 주소창 없이 앱처럼 열려요.</>,
            ]
          : [
              <>오른쪽 위 <b>⋮ 메뉴</b>를 눌러요.</>,
              <>
                <b>앱 설치</b>를 눌러요. <b>홈 화면에 추가</b>만 보이면 누른 뒤 <b>설치</b>를 골라요. 바로가기 만들기를 고르면 브라우저로 열려요.
              </>,
              <>홈 화면(또는 앱 목록)의 메신저 아이콘으로 열어요. 주소창 없이 앱처럼 열려요.</>,
            ];
      break;
    case 'ios-share':
      steps = iosShare(plan.browser);
      break;
    case 'ios-safari-only':
      steps = [<><b>Safari</b>를 열고 이 메신저 주소로 들어가요.</>, ...iosShare('safari')];
      break;
    case 'open-outside':
      steps =
        plan.os === 'ios'
          ? [
              <>
                {IN_APP_NAMES[plan.app]}의 <b>⋯</b>(또는 공유) 메뉴에서 <b>Safari로 열기</b>나 <b>다른 브라우저로 열기</b>를 눌러요.
              </>,
              ...iosShare('safari'),
            ]
          : [<>{IN_APP_NAMES[plan.app]}의 <b>⋮</b> 메뉴에서 <b>다른 브라우저로 열기</b>를 눌러 Chrome으로 열어요.</>, <>Chrome에서 이 안내의 <b>설치</b>를 눌러요.</>];
      break;
    default:
      return null;
  }
  return (
    <ol className="install-steps">
      {steps.map((step, index) => (
        <li key={index}>{step}</li>
      ))}
    </ol>
  );
}

function InstallGuide({ plan, onClose }: { plan: InstallPlan; onClose: () => void }) {
  const titleId = useId();
  return (
    <Modal open onClose={onClose} labelledBy={titleId} size="small">
      <DialogHead titleId={titleId} title="홈 화면에 메신저 추가" onClose={onClose} />
      <div className="dlg-body">
        <p className="hint install-why">{headline(plan)} 앱 스토어나 별도 설치 파일은 필요 없어요.</p>
        <InstallSteps plan={plan} />
        <a className="btn sm ghost block" href={MOBILE_GUIDE} target="_blank" rel="noopener">
          그림과 함께 자세히 보기
        </a>
      </div>
    </Modal>
  );
}

/**
 * The one action that adds the messenger to this phone's home screen: the browser's install dialog where
 * it offered one, opening the messenger outside an in-app browser, or the steps (iPhone: no web page can
 * open the share sheet's 홈 화면에 추가 for the person).
 */
export function InstallAction({ plan, install, block }: { plan: InstallPlan; install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>; block?: boolean }) {
  const toast = useToast();
  const [guide, setGuide] = useState(false);
  if (plan.kind === 'installed' || plan.kind === 'desktop') return null;
  if (plan.kind === 'prompt') {
    return (
      <Button
        size="sm"
        variant="primary"
        block={block}
        onClick={() =>
          void install().then((outcome) => {
            if (outcome === 'accepted') toast('설치하고 있어요. 홈 화면(또는 앱 목록)의 메신저 아이콘으로 열어 주세요');
          })
        }
      >
        홈 화면에 설치
      </Button>
    );
  }
  if (plan.kind === 'open-outside' && plan.url) {
    const target = plan.os === 'ios' ? 'Safari에서 열기' : plan.url.startsWith('intent:') ? 'Chrome에서 열기' : '브라우저에서 열기';
    return (
      <a className={['btn sm primary', block && 'block'].filter(Boolean).join(' ')} href={plan.url}>
        {target}
      </a>
    );
  }
  return (
    <>
      <Button size="sm" variant="primary" block={block} onClick={() => setGuide(true)}>
        방법 보기
      </Button>
      {guide ? <InstallGuide plan={plan} onClose={() => setGuide(false)} /> : null}
    </>
  );
}

/**
 * Web on a phone, in a browser tab: a hint above the conversations to add the messenger to the home
 * screen, with the action that does it there. Closing it keeps it closed; 설정 keeps the same action.
 */
function InstallHint({ onDismiss }: { onDismiss: () => void }) {
  const { plan, install, installedHere } = useInstallPlan();
  if (plan.kind === 'installed' || plan.kind === 'desktop') return null;
  return (
    <div className="notice notify-hint install-hint" role="status">
      <Icon name="download" size="sm" />
      {installedHere ? (
        <span>설치했어요. 홈 화면(또는 앱 목록)의 메신저 아이콘으로 열어 주세요.</span>
      ) : (
        <>
          <span>{headline(plan)}</span>
          <InstallAction plan={plan} install={install} />
        </>
      )}
      <IconButton icon="x" iconSize="sm" label="설치 안내 닫기" onClick={onDismiss} />
    </div>
  );
}

/**
 * The hints above the conversations, one at a time. On a phone in a browser tab installing comes first: it
 * is where an iPhone gets notifications at all. Once that hint is closed an Android browser tab can still
 * turn notifications on; an iPhone tab cannot.
 */
export function ListHints() {
  const env = useAppEnv();
  const [installClosed, setInstallClosed] = useState(installHintDismissed);
  const { plan } = useInstallPlan();
  if (env.platform.kind !== 'web') return null;
  const onPhoneTab = plan.kind !== 'installed' && plan.kind !== 'desktop';
  if (onPhoneTab && !installClosed) {
    return (
      <InstallHint
        onDismiss={() => {
          dismissInstallHint();
          setInstallClosed(true);
        }}
      />
    );
  }
  if (needsHomeScreenForNotifications()) return null;
  return <NotifyHint />;
}
